import { Button, ButtonsGroup, Heading, InputSelect, PageHeader, PageWrapper } from "#/atoms";
import { FC, memo, useCallback, useMemo, useState } from "react";

import { Column } from "react-table";
import { CustomTable } from "#/molecules";
import { NATURAL } from "@/theme";
import { WorkerInvoicesProps } from "./WorkerInvoices.interface";
import { formatShortDateString } from "@/helpers";

const WorkerInvoices: FC<WorkerInvoicesProps> = ({ loading, onApprove, onDelete, onMarkAsPaid, onNewInvoice, workerInvoices }) => {
  enum InvoiceListType {
    TOBEAPPROVED = 1,
    TOBEMARKED = 2,
    PAID = 3
  }

  const mainData = useMemo(() => workerInvoices.map((workerInvoice) => ({
    amount: workerInvoice.amount,
    approved: workerInvoice.approved,
    dueDate: workerInvoice.dueDate,
    fileName: workerInvoice.fileName,
    filePath: workerInvoice.filePath,
    id: workerInvoice.workerInvoiceId,
    invoiceNumber: workerInvoice.invoiceNumber,
    name: workerInvoice.userName,
    paid: workerInvoice.paid
    
  })), [workerInvoices]);

  const [invoicesToApprove, setInvoicesToApprove] = useState<string[]>([]);
  const [invoicesToMark, setInvoicesToMark] = useState<string[]>([]);

  const data = useMemo(() => mainData.filter((item) => !item.approved && !item.paid), [workerInvoices]);
  const approvedData = useMemo(() => mainData.filter((item) => item.approved && !item.paid), [workerInvoices]);
  const paidData = useMemo(() => mainData.filter((item) => item.paid), [workerInvoices]);

  const handleInputChange = useCallback((isChecked : boolean, value : string, type : number) => {
    if (isChecked) {
      type === InvoiceListType.TOBEMARKED? setInvoicesToMark([...invoicesToMark, value]) : setInvoicesToApprove([...invoicesToApprove, value])
    } else {
      type === InvoiceListType.TOBEMARKED ? setInvoicesToMark(invoicesToMark.filter((item) => item !== value)) :setInvoicesToApprove(invoicesToApprove.filter((item) => item !== value));
    }
  }, [invoicesToApprove]);

  const handleSelectAll = () => {
    if (invoicesToApprove.length === data.length) {
      setInvoicesToApprove([]);
    } else {
      setInvoicesToApprove(data.map((invoice) => invoice.id));
    }
  }

  
  const handleDelete = useCallback((id: string) => {
    onDelete(id);
  }, []);
  
  const handleApprove = useCallback((id: string) => {
    if (id === 'selected') {
      onApprove(invoicesToApprove);
    }
    else {
      onApprove([id]);
    }
  }, [invoicesToApprove]);
  
  const handleMarkAsPaid = useCallback((id: string) => {
    if (id === 'selected') {
      onMarkAsPaid(invoicesToMark);
    }
    else {
      onMarkAsPaid([id]);
    }
  }, [invoicesToMark]);

  const getChecked = (val: string, type: InvoiceListType) => {
    if (type === InvoiceListType.TOBEMARKED)
      return invoicesToMark.includes(val);
    else
      return invoicesToApprove.includes(val);
  }
  
  const getColumns = (type?: InvoiceListType) => { 
    let cols : Column[] = [
    {
      Header: 'Invoice Number',
      accessor: 'invoiceNumber',
    },
    {
      Header: 'User Name',
      accessor: 'name',
    },
    {
      Header: 'Amount',
      accessor: 'amount',
    },
    {
      Cell: ({ value }) => (
              <p> { formatShortDateString(value) }</p>
            ),
      Header: 'Due Date',
      accessor: 'dueDate',
    },
    {
      Header: 'Link',
      accessor: 'fileName',
    },
  ] 
  
    
  if (type === InvoiceListType.TOBEAPPROVED || type === InvoiceListType.TOBEMARKED) {
    cols = [
    {
        Cell: (props) => (
          <InputSelect
            type="checkbox"
            checked={getChecked(props.value, type)}
            name="invoices"
            value={props.value}
            onChangeValue={(checked, value) => handleInputChange(checked, value, type)}
          />
        ),
        Header: '',
        accessor: 'id',
      },
      ...cols
    ];
  }
    
  if (type === InvoiceListType.TOBEAPPROVED) {
    cols = [
    ...cols,
      {
        Cell: ({ value }) => {
            return (
              <ButtonsGroup fullWidth>
                <Button
                actionType='outline'
                onClick={() => handleDelete(value)}
                buttonSize="sm"
                >
                  Delete
                </Button>
                <Button
                  actionType='solid'
                  onClick={() => handleApprove(value)}
                  buttonSize="sm"
                  >
                    Approve
                </Button>
              </ButtonsGroup>
            );
          },
        Header: '',
        accessor: 'id',
        id: 'button'
      },
    ];
  } else if (type === InvoiceListType.TOBEMARKED) {
    cols = [
    ...cols,
      {
        Cell: ({ value }) => {
            return (
              <ButtonsGroup fullWidth>
                <Button
                actionType='solid'
                onClick={() => handleMarkAsPaid(value)}
                buttonSize="sm"
                >
                  Mark as Paid
                </Button>
              </ButtonsGroup>
            );
          },
        Header: '',
        accessor: 'id',
        id: 'button'
      },
    ];
  }

    return cols as Column[];

  };

  return (
    <PageWrapper>
      <PageHeader>
        <Heading scale="h1" fontColor={NATURAL.K}>Photographer Invoices</Heading>
        <ButtonsGroup>
          <Button onClick={handleSelectAll}>{ invoicesToApprove.length > 0 ? 'Deselect All' : 'Select All'}</Button>
          {/* <Button onClick={handleSelectAllApproved}>Select All Approved</Button> */}
          <Button onClick={() => handleApprove('selected')}>Mark As Approved</Button>
          <Button onClick={() => handleMarkAsPaid('selected')}>Mark As Paid</Button>
          <Button onClick={onNewInvoice}>Find For Worker</Button>
        </ButtonsGroup>
      </PageHeader>
      <Heading scale="h2" fontColor={NATURAL.K}>Unpaid Invoices</Heading>
      <CustomTable columns={getColumns(InvoiceListType.TOBEAPPROVED)} data={data} rowProps={({ backgroundColor: '#FF2A21', color: 'white' })}  loading={loading} pagination={{ pageIndex: 0, pageSize: 10 }} />
      <Heading scale="h2" fontColor={NATURAL.K}>Approved Invoices</Heading>
      <CustomTable columns={getColumns(InvoiceListType.TOBEMARKED)} data={approvedData} rowProps={({ backgroundColor: 'orange', color: 'white' })}  loading={loading} pagination={{ pageIndex: 0, pageSize: 10 }} />
      <Heading scale="h2" fontColor={NATURAL.K}>Paid Invoices</Heading>
      <CustomTable columns={getColumns(InvoiceListType.PAID)} data={paidData} rowProps={({ backgroundColor: '#33AA33', color: 'white' })}  loading={loading} pagination={{ pageIndex: 0, pageSize: 10 }} />
    </PageWrapper>
  );
};

export default memo(WorkerInvoices);